import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { XIcon, ClockIcon, ShoppingBagIcon, MessageSquareIcon, MapPinIcon, DropletIcon } from 'lucide-react';
import { Perfume } from '../../data/retail';
import { money } from '../../data/food';
import { MerchantBadge } from '../MerchantBadge';
import { merchantFor } from '../../data/merchants';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../AuthContext';

/**
 * The full fragrance. Size and price are a real choice here too — a detail
 * view that can only be read, not acted on, sends the shopper back to the card
 * to do the one thing they opened it for.
 */
export function PerfumeDetailModal({
  id,
  title,
  location,
  images,
  perfume,
  initialSizeId,
  onClose
}: {
  id: string;
  title: string;
  location: string;
  images: string[];
  perfume: Perfume;
  initialSizeId?: string;
  onClose: () => void;
}) {
  const {
    requireAuth
  } = useAuth();
  const {
    add,
    setOpen
  } = useCart();
  const navigate = useNavigate();
  const available = perfume.sizes.filter(s => s.inStock);
  const canOrder = perfume.ordering !== false && available.length > 0;
  const [sizeId, setSizeId] = useState(initialSizeId ?? (available.find(s => s.note) ?? available[0])?.id);
  const size = perfume.sizes.find(s => s.id === sizeId);
  const house = perfume.house;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);
  return createPortal(<div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6" data-id="element-3339" data-mp="3bc38kct5xai2">
      <div className="absolute inset-0 bg-dark-navy/50 backdrop-blur-sm" onClick={onClose} data-id="element-3340" data-mp="3ganzflmiyhtu" />
      <motion.div initial={{
      opacity: 0,
      y: 24,
      scale: 0.97
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} transition={{
      duration: 0.22,
      ease: [0.23, 1, 0.32, 1]
    }} role="dialog" aria-modal="true" aria-label={title} className="relative w-full sm:max-w-3xl bg-white rounded-t-3xl sm:rounded-3xl shadow-large overflow-hidden flex flex-col sm:flex-row max-h-[92vh] sm:max-h-[86vh] sm:h-[600px]" data-id="element-3341" data-mp="33dhrwk2tzflx">
        <button onClick={onClose} aria-label="Close" className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center text-slate-600 hover:text-dark-navy shadow-sm transition-colors duration-150 ease-out" data-id="element-3342" data-mp="2zcxurgi9cbzs">
          <XIcon className="w-4 h-4" data-id="element-3343" data-mp="9m1c1fij8b5p" />
        </button>

        <div className="relative flex-shrink-0 h-44 sm:h-auto sm:w-[42%]" style={{
        backgroundColor: perfume.tint
      }} data-id="element-3344" data-mp="h5k9urwfq8yc">
          <img src={images[0]} alt={title} className="w-full h-full object-cover" data-id="element-3345" data-mp="855cuva3z5f0" />
        </div>

        <div className="flex-1 flex flex-col min-w-0" data-id="element-3346" data-mp="c7sajctgnkk4">
          <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 space-y-5" data-id="element-3347" data-mp="zwbdrdxnl75c">
            <div data-id="element-3348" data-mp="zyeearwfmiob">
              <MerchantBadge merchant={merchantFor(house, 'Fragrance house')} showKind data-id="element-3349" data-mp="14vejz1pddjew" />
              <h2 className="font-display font-bold text-xl text-dark-navy mt-3 leading-snug" data-id="element-3350" data-mp="r2yg5yon9hcx">
                {title}
              </h2>
              <p className="text-[13px] font-semibold text-slate-500 mt-1" data-id="element-3351" data-mp="v3ic4yxheexh">
                {perfume.concentration} · {perfume.family}
              </p>
              <p className="inline-flex items-center gap-1.5 text-[12px] text-slate-400 mt-2" data-id="element-3352" data-mp="1nm5pfs68kpo0">
                <MapPinIcon className="w-3.5 h-3.5" data-id="element-3353" data-mp="3w38hmyajhm47" />
                {location}
              </p>
            </div>

            <div data-id="element-3354" data-mp="3r33zl3tzgjbv">
              <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400" data-id="element-3355" data-mp="3r2kghjsljow9">
                Notes
              </h3>
              <div className="flex flex-wrap gap-1.5 mt-2" data-id="element-3356" data-mp="3i3770r4h30bj">
                {perfume.notes.map(n => <span key={n} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-50 text-[12px] font-semibold text-slate-600" data-id="element-3357" data-mp="insp2dc490fx">
                    <DropletIcon className="w-3 h-3 text-slate-300" data-id="element-3358" data-mp="8xh7hnziesqk" />
                    {n}
                  </span>)}
              </div>
              <p className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-400 mt-3" data-id="element-3359" data-mp="4ukv4f0kbsv2">
                <ClockIcon className="w-3.5 h-3.5" data-id="element-3360" data-mp="11gei6pvn2cwt" />
                Lasts {perfume.longevity}
              </p>
            </div>

            {/* Size and price — actionable, not just informational */}
            <div data-id="element-3361" data-mp="2k9obgvfx60fv">
              <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400" data-id="element-3362" data-mp="2k96699ba4bvt">
                Bottle size
              </h3>
              <div className="space-y-2 mt-2" data-id="element-3363" data-mp="2o9q30brxxdhf">
                {perfume.sizes.map(s => <button key={s.id} disabled={!s.inStock} onClick={() => setSizeId(s.id)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl border text-left transition-colors duration-150 ease-out ${!s.inStock ? 'border-slate-100 cursor-not-allowed' : sizeId === s.id ? 'border-dark-navy bg-dark-navy/[0.03]' : 'border-slate-200 hover:border-slate-400'}`} data-id="element-3364" data-mp="2tb5no2q7hhza">
                    <span className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${sizeId === s.id && s.inStock ? 'border-dark-navy' : 'border-slate-300'}`} data-id="element-3365" data-mp="3lzb8eph9f6z4">
                      {sizeId === s.id && s.inStock && <span className="w-2 h-2 rounded-full bg-dark-navy" data-id="element-3366" data-mp="1vnwm8an7yc3q" />}
                    </span>
                    <span className={`text-sm font-semibold flex-1 truncate ${s.inStock ? 'text-dark-navy' : 'text-slate-300'}`} data-id="element-3367" data-mp="1qu28roy7itd3">
                      {s.label}
                      {!s.inStock && ' · sold out'}
                    </span>
                    {s.note && s.inStock && <span className="px-2 py-0.5 rounded-full bg-slate-100 text-[11px] font-bold text-slate-500 flex-shrink-0" data-id="element-3368" data-mp="29m3cb4lxf7su">
                        {s.note}
                      </span>}
                    <span className={`text-sm font-bold flex-shrink-0 ${s.inStock ? 'text-dark-navy' : 'text-slate-300'}`} data-id="element-3369" data-mp="f9dh8jjtnsjv">
                      {money(s.price)}
                    </span>
                  </button>)}
              </div>
            </div>

            {!canOrder && <p className="px-4 py-3 bg-slate-50 rounded-2xl text-[12px] text-slate-500 leading-relaxed" data-id="element-3370" data-mp="2h41j31cq44sd">
                {house} completes orders in conversation rather than online.
                Message them to buy, reserve, or ask for a sample.
              </p>}
          </div>

          <div className="flex items-center gap-3 px-5 sm:px-6 py-4 border-t border-slate-100 flex-shrink-0" data-id="element-3371" data-mp="2d27nh7hdxqz2">
            {canOrder ? <button onClick={() => {
            if (!size) return;
            requireAuth(() => {
              add({
                listingId: id,
                merchant: house,
                title,
                image: images[0],
                summary: `${size.label} · ${perfume.concentration}`,
                unitPrice: size.price,
                qty: 1
              });
              onClose();
              setOpen(true);
            });
          }} className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-primary-indigo text-white text-sm font-bold rounded-2xl hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-3372" data-mp="235l3sm0sp5ym">
                <ShoppingBagIcon className="w-4 h-4" data-id="element-3373" data-mp="1xkog7m3llte9" />
                Add {size?.label} · {size && money(size.price)}
              </button> : <button onClick={() => requireAuth(() => navigate(`/messages?to=${encodeURIComponent(house)}&about=${encodeURIComponent(title)}`))} className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-primary-indigo text-white text-sm font-bold rounded-2xl hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-3374" data-mp="1so7iq1aie2oi">
                <MessageSquareIcon className="w-4 h-4" data-id="element-3375" data-mp="3uhamijhw0due" />
                Message {house}
              </button>}
          </div>
        </div>
      </motion.div>
    </div>, document.body);
}